import { Injectable, signal } from '@angular/core';

/**
 * Tracks whether this browser tab/window currently has user focus.
 *
 * This service is intentionally ignorant of database, WebSocket, or any other
 * application logic. It is purely a focus-state signal that other services can
 * react to via Angular's effect() or computed() primitives.
 *
 * Focus rules:
 *   - window 'focus' event  → this window/tab is now active
 *   - window 'blur'  event  → this window/tab lost focus (another window is active)
 *   - document 'visibilitychange' → tab switched within the same browser window
 *
 * Initialised synchronously from document.hasFocus() so the signal is correct
 * from the very first read, even before any events have fired.
 */
@Injectable({ providedIn: 'root' })
export class TabFocusService {
  private readonly _isFocused = signal(document.hasFocus());

  /** True when this tab/window currently has user focus. */
  readonly isFocused = this._isFocused.asReadonly();

  constructor() {
    // Window gained focus — covers:
    //   • switching back to this browser window from another app or browser window
    //   • switching back to this tab within a window (some browsers)
    window.addEventListener('focus', () => {
      this._isFocused.set(true);
    });

    // Window lost focus — covers:
    //   • switching to another browser window or application
    //   • some browsers fire this when switching away from this tab within a window
    window.addEventListener('blur', () => {
      this._isFocused.set(false);
    });

    // Tab visibility changed — covers switching tabs within the same browser
    // window (where the window itself doesn't blur, only the document hides).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._isFocused.set(false);
      } else {
        // Became visible; re-check actual window focus because the window may
        // or may not have focus (e.g. the user may have alt-tabbed away and
        // back, in which case 'focus' and this event fire together).
        this._isFocused.set(document.hasFocus());
      }
    });
  }
}
